import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { Platform, View, TouchableWithoutFeedback } from "react-native";
import * as Linking from "expo-linking";
import { WebView, WebViewMessageEvent } from "react-native-webview";
import styled from "styled-components/native";
import { CustomWebRef, WebviewProps } from "./types";
import { useTheme } from "styled-components/native";
import Loader from "../Loader";
import { useAppSettings } from "@redux/slices/globalSlice";
import { useWebViewNativeLifecycle } from "../../../hooks/useWebViewNativeLifecycle";
import { MediaType } from "@utils/constants";

const isThirdPartyWidgetUrl = (u?: string | null) => {
  if (!u) return false;
  return /(google\.com\/recaptcha|recaptcha\.net|gstatic\.com|googleapis\.com|elfsight\.com|elfsightcdn\.com)/i.test(
    u
  );
};

const CustomWeb = forwardRef<CustomWebRef, WebviewProps>(
  (
    {
      url = "",
      height,
      width,
      isSmallDeviceWidth,
      injectedJavascript = "",
      injectedCSS = "",
      deviceWidth = 0,
      resizeImage = false,
      extraSmallImage = false,
      onMessage,
      showExtraBottomView = false,
      isFromPDP = true,
      errorCallback,
      isClickable = true,
      enableReload = false,
      allowScaleScript = true,
      showLoader = false,
      videoType = "internal",
      mediaType = "",
      fromExternalScreen = false,
      mediaPlaybackRequiresUserAction = false,
      extraQuery,
      onNavigationStateChange,
    }: WebviewProps,
    ref
  ) => {
    const theme = useTheme();
    const webviewRef = useRef<WebView>(null);
    const { isMountedRef } = useWebViewNativeLifecycle(webviewRef);
    const iframeRef = useRef<HTMLIFrameElement | null>(null);

    useImperativeHandle(ref, () => ({
      goBack: () => {
        if (Platform.OS === "web") {
          try {
            iframeRef.current?.contentWindow?.history?.back();
          } catch (e) {
            // Cross-origin iframe: history navigation is not accessible.
          }
          return;
        }
        webviewRef.current?.goBack?.();
      },
    }));
    const actualUrl = url ?? "";
    const isWebViewCachingEnabled =
      useAppSettings()?.is_web_view_caching_enabled ?? true;
    const calculatedHeight =
      height ??
      (isSmallDeviceWidth
        ? extraSmallImage
          ? deviceWidth
          : resizeImage
          ? isFromPDP
            ? deviceWidth / 1.5
            : "100%"
          : deviceWidth
        : "100%");
    const [loading, setLoading] = useState(true);

    const handleLoadEnd = () => {
      if (!isMountedRef.current) return;
      setLoading(false);
    };

    const handleNavigationStateChange = (navState: any) => {
      if (!navState.loading && navState.url && navState.url !== "about:blank") {
        handleLoadEnd();
      }
      onNavigationStateChange?.(navState);
    };

    const calculatedWidth =
      width ??
      (isSmallDeviceWidth && theme.isMobile
        ? extraSmallImage
          ? deviceWidth
          : resizeImage
          ? isFromPDP
            ? deviceWidth / 1.5
            : 250
          : deviceWidth
        : // Fix for external players ...
          "100%");

    const handleNavigation = (event: any) => {
      const { url: mUrl, isTopFrame } = event;

      if (mediaType !== "") return true;
      if (
        mUrl === "" ||
        mUrl === undefined ||
        mUrl === null ||
        mUrl === "about:blank"
      ) {
        return true;
      }

      // Never intercept reCAPTCHA / gstatic / Elfsight resources. These load in
      // sub-frames and MUST reach WKWebView untouched for the captcha to work.
      if (isThirdPartyWidgetUrl(mUrl)) return true;

      // On iOS, onShouldStartLoadWithRequest ALSO fires for iframes/sub-frames
      // (unlike Android, where it is effectively main-frame only). reCAPTCHA runs
      // inside an iframe, so only ever gate the top frame here.
      if (Platform.OS === "ios" && isTopFrame === false) return true;

      if (
        event.navigationType === "click" &&
        mUrl.includes("vpg_type=appexternal")
      ) {
        Linking.openURL(mUrl).catch((err) =>
          console.error("Failed to open URL:", err)
        );
        return false;
      } else if (Platform.OS !== "web" && fromExternalScreen) {
        const originalUrl = mUrl;
        if (originalUrl.includes("hbmenu=false")) {
          return true;
        }
        const murl = new URL(originalUrl);
        murl.searchParams.set("hbmenu", "false");
        const redirectScript = `window.location.href = "${murl.toString()}"; true;`;
        // @ts-ignore
        if (!isMountedRef.current) return false;
        webviewRef.current?.injectJavaScript?.(redirectScript);

        return false;
      }
      return true;
    };

    const injectionScript = `
  if(${allowScaleScript}){
    (function() {
      var meta = document.createElement('meta');
      meta.setAttribute('name', 'viewport');
      meta.setAttribute('content', 'width=device-width, initial-scale=1');
      document.getElementsByTagName('head')[0].appendChild(meta);
    })();
  }
  

  (function() {
    var style = document.createElement('style');
    style.textContent = ${JSON.stringify(injectedCSS)};
    document.head.appendChild(style);

    var script = document.createElement('script');
    script.type = 'text/javascript';
    script.textContent = ${JSON.stringify(injectedJavascript)};
    document.documentElement.appendChild(script);

    var pointerScript = document.createElement('script');
    pointerScript.type = 'text/javascript';
    pointerScript.textContent = ${JSON.stringify(
      isClickable
        ? 'document.body.style.pointerEvents = "auto";'
        : 'document.body.style.pointerEvents = "none";'
    )};
    document.documentElement.appendChild(pointerScript);
    
    if ('${mediaType}' === 'video') {
      window.addEventListener('load', function() {
        setTimeout(function() {
          if (window.ReactNativeWebView) {
            window.ReactNativeWebView.postMessage('CONTENT_LOADED');
          }
        }, 1500);
      });
      
      // Also check for video elements
      var checkVideo = setInterval(function() {
        var videos = document.getElementsByTagName('video');
        var iframes = document.getElementsByTagName('iframe');
        if (videos.length > 0 || iframes.length > 0) {
          clearInterval(checkVideo);
          setTimeout(function() {
            if (window.ReactNativeWebView) {
              window.ReactNativeWebView.postMessage('CONTENT_LOADED');
            }
          }, 1000);
        }
      }, 500);
      
      // Timeout after 5 seconds
      setTimeout(function() {
        clearInterval(checkVideo);
      }, 5000);
    }
    
    if (${enableReload}) {
      window.ReactNativeWebView.postMessage(window.document.baseURI);
    }
  })();

  true;
`;
    const [reloadKey, setReloadKey] = React.useState(0);
    const isMp4 =
      actualUrl?.toLowerCase().endsWith(".mp4") || videoType === "native";
    useEffect(() => {
      if (!isMountedRef.current) return;
      setReloadKey((prev) => prev + 1);
    }, [actualUrl]);
    const bgColor = theme["core-02"];
    const videoHtml = `
  <html>
     <style>
        html, body {
          margin: 0;
          height: 100%;
        }
        video {
          width: 100%;
          height: 100%;
          object-fit: contain;
          background: ${bgColor};
        }
     </style>
     <body style="margin:0;padding:0;display:flex;justify-content:center;align-items:center;height:100%;">
       <video id="videoPlayer" width="100%" height="100%" autoplay muted loop playsinline>
         <source src="${actualUrl}" type="video/mp4" />
         Your browser does not support the video tag.
       </video>
       
       <script>
         const video = document.getElementById('videoPlayer');
         
  
         video.addEventListener('loadeddata', () => {
           console.log('Video data loaded');
           if (window.ReactNativeWebView) {
             window.ReactNativeWebView.postMessage('VIDEO_LOADED');
           }
         });
         
         video.addEventListener('click', () => {
           if (video.paused) {
             video.play();
           } else {
             video.pause();
           }
         });
       </script>
     </body>
   </html>
 `;

    const getPDFContent = `
    <html>
        <head>
          <meta name="viewport" content="width=device-width, initial-scale=1.0">
        </head>
        <body>
          <embed 
            src="${actualUrl}"
            type="application/pdf"
            width="100%"
            height="100%"
          />
        </body>
      </html>
    `;

    const handleWebViewError = (syntheticEvent: any) => {
      if (!isMountedRef.current) return;
      const { nativeEvent } = syntheticEvent;
      if (errorCallback) {
        errorCallback(nativeEvent);
      }
    };

    const onMessageVideo = (event: WebViewMessageEvent) => {
      const message = event.nativeEvent.data;

      if (message === "VIDEO_LOADED" || message === "CONTENT_LOADED") {
        handleLoadEnd();
        return;
      }

      // Issue does not occur in iOS
      const isAndroid = Platform.OS === "android";
      if (!isAndroid) return;
      if (message === "about:blank") {
        if (!isMountedRef.current) return;
        setReloadKey((prevKey) => prevKey + 1);
      }
    };

    const isGemprintUrl = actualUrl?.toLowerCase().includes("gemprint");
    const handleContentProcessTerminate = useCallback(() => {
      if (!isMountedRef.current) return;
      console.warn("WebView process terminated, reloading...");
      if (!isMountedRef.current) return;
      setReloadKey((prev) => prev + 1);
    }, [isMountedRef]);

    const handleRenderProcessGone = useCallback((event: any) => {
      if (!isMountedRef.current) return;
      console.warn(
        "WebView render process gone, reloading...",
        event.nativeEvent
      );
      setReloadKey((prev) => prev + 1);
    }, []);

    const finalUrl = React.useMemo(() => {
      if (
        !url ||
        isMp4 ||
        (mediaType === "pdf" && Platform.OS === "ios" && !isGemprintUrl)
      ) {
        return url;
      }
      if (!extraQuery) return url;
      const separator = url.includes("?") ? "&" : "?";
      return `${url}${separator}${extraQuery}`;
    }, [url, extraQuery, isMp4, mediaType]);

    const sourceContent = isMp4
      ? { html: videoHtml }
      : mediaType === "pdf" && Platform.OS === "ios" && !isGemprintUrl
      ? { html: getPDFContent }
      : { uri: finalUrl };

    return (
      <Container style={{ pointerEvents: isClickable ? "auto" : "none" }}>
        {showLoader && loading && <Loader />}
        {Platform.OS === "web" ? (
          // @ts-ignore
          <iframe
            ref={iframeRef}
            id="diamond-filter-iframe"
            style={{
              height: calculatedHeight,
              width: calculatedWidth,
              flex: 1,
              borderWidth: 0,
            }}
            {...(isMp4
              ? {
                  srcDoc: videoHtml,
                }
              : {
                  src: finalUrl,
                })}
            className="w-full h-96 border border-gray-200 rounded"
            onLoad={() => {
              handleLoadEnd();
            }}
          />
        ) : (
          <WebView
            ref={webviewRef}
            key={reloadKey}
            // @ts-ignore
            source={sourceContent}
            style={{
              height: calculatedHeight,
              width: calculatedWidth,
              flex: 1,
              resizeMode: "cover",
            }}
            {...(Platform.OS === "ios" && { decelerationRate: "normal" })}
            {...(Platform.OS === "android" && {
              nestedScrollEnabled: true,
              scrollEnabled: true,
              // "hardware" creates a dedicated GPU surface. MediaTek drivers
              // SIGSEGV (near-null 0xf0) when that surface is drawn after detach.
              androidLayerType:
                mediaType === MediaType.PDF || mediaType === MediaType.IMAGE
                  ? "software"
                  : "none",
              overScrollMode: "never",
            })}
            onMessage={onMessage ? onMessage : onMessageVideo}
            injectedJavaScript={injectionScript}
            javaScriptEnabled={true}
            cacheEnabled={isWebViewCachingEnabled}
            scalesPageToFit={Platform.OS === "ios"}
            domStorageEnabled={true}
            allowsInlineMediaPlayback={true}
            mediaPlaybackRequiresUserAction={mediaPlaybackRequiresUserAction}
            automaticallyAdjustContentInsets={false}
            onShouldStartLoadWithRequest={handleNavigation}
            onError={handleWebViewError}
            onHttpError={handleWebViewError}
            onLoadStart={() => {
              if (!isMountedRef.current) return;
              setLoading(true);
            }}
            onLoadEnd={() => {
              handleLoadEnd();
            }}
            onNavigationStateChange={handleNavigationStateChange}
            onContentProcessDidTerminate={handleContentProcessTerminate}
            onRenderProcessGone={handleRenderProcessGone}
          />
        )}
        {showExtraBottomView && !theme.isLargeScreen && (
          <BottomView>
            <TouchableWithoutFeedback onPress={() => {}}>
              <TouchOverlay style={{ pointerEvents: "box-only" }} />
            </TouchableWithoutFeedback>
          </BottomView>
        )}
      </Container>
    );
  }
);

CustomWeb.displayName = "CustomWeb";

const Container = styled(View)`
  flex: 1;
`;

const BottomView = styled(View)`
  height: ${Platform.OS === "ios" ? "60px" : "70px"};
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 10;
  ${Platform.OS === "android" && `elevation: 5;`}
`;

const TouchOverlay = styled(View)`
  flex: 1;
  background-color: transparent;
  ${Platform.OS === "android" && `elevation: 5;`}
`;

export default CustomWeb;
